import { type ReactNode, useEffect } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { Control, FieldErrors } from 'react-hook-form'
import { Controller, useController, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  Flex,
  Input,
  SimpleGrid,
  Stack,
  Textarea,
} from '@enechain/ecloud-designsystem'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { CompactCivilDateField } from '~/components/common/CompactCivilDateField'
import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
} from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import type { CivilDate } from '~/models/civilDate'
import { plantPowerTypeLabel } from '~/pages/AdminOcctoMaster/tabs/PlantMaster/models/labels'
import { CharCount } from '~/pages/AdminPlantUnitMaster/components/CharCount'
import {
  type PendingSite,
  SiteSelectField,
} from '~/pages/AdminPlantUnitMaster/components/SiteSelectField'
import { generatorCategoryLabel } from '~/pages/AdminPlantUnitMaster/models/labels'
import { plantCandidates } from '~/pages/AdminPlantUnitMaster/models/unitCandidates'
import {
  GENERATOR_IDENTIFIER_MAX_LENGTH,
  POWER_SOURCE_ID_MAX_LENGTH,
  type PlantUnitFormInput,
  UNIT_NAME_ABBR_MAX_LENGTH,
  UNIT_NAME_MAX_LENGTH,
  UNIT_NOTE_MAX_LENGTH,
} from '~/pages/AdminPlantUnitMaster/models/unitForm'
import {
  GENERATOR_CATEGORY_OPTIONS,
  UNIT_POWER_TYPE_OPTIONS,
} from '~/pages/AdminPlantUnitMaster/models/unitRows'

type SiteOption = { id: string; siteName: string; areaCode: AreaCode }
type PlantOption = {
  id: string
  plantCode: string
  plantName: string
  areaCode: AreaCode
}

type Props = {
  control: Control<PlantUnitFormInput>
  errors: FieldErrors<PlantUnitFormInput>
  /** 保存中など、編集 session を固定する間は全ての入力を無効化する。 */
  disabled?: boolean
  sites: readonly SiteOption[]
  /** 系統コード (広域機関の発電所) の候補。 */
  plants: readonly PlantOption[]
  /** 登録ダイアログだけ true (詳細ページの発電所は「発電所」タブで足す)。 */
  allowAddSite?: boolean
  pendingSite?: PendingSite | undefined
  onSiteAdded?: (site: PendingSite) => void
  /** 欄の id の接頭辞 (画面内で一意にする)。 */
  idPrefix: string
  /**
   * エリア欄 (候補を絞るだけの欄) を出すか。登録ダイアログは出す。詳細ページは出さず、
   * 発電所は絞らず、系統コードは選んだ発電所のエリアで絞る。
   */
  showAreaFilter?: boolean
  /** エリア欄を選び直した時 (フォームへの反映後)。値は AreaCode (未選択は UNSPECIFIED)。 */
  onAreaChanged?: (areaCode: AreaCode) => void
  /** 発電所を選び直した時 (フォームへの反映後)。値は発電所 id。 */
  onSiteChanged?: (siteId: string | undefined) => void
  /**
   * 電源種別の補足の出し方。登録ダイアログは欄の下の補足 (hint)、詳細ページの編集中は項目名に
   * hover で出す (mock の UnitBasicFields と同じ)。
   */
  powerTypeNoteAs?: 'hint' | 'hover'
}

export const TextField = ({
  control,
  errors,
  name,
  id,
  label,
  hint,
  required = false,
  max,
  width,
  inputMode,
  disabled = false,
  hideLabel = false,
}: {
  control: Control<PlantUnitFormInput>
  errors: FieldErrors<PlantUnitFormInput>
  name:
    | 'unitName'
    | 'unitNameAbbr'
    | 'authorizedOutputKw'
    | 'receivingPointId'
    | 'powerSourceId'
    | 'generatorIdentifier'
  id: string
  label: string
  hint?: string
  required?: boolean
  max?: number
  width?: string
  inputMode?: 'numeric'
  disabled?: boolean
  hideLabel?: boolean
}): React.ReactElement => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FieldRoot
          alignItems="stretch"
          invalid={errors[name] !== undefined}
          required={required}
        >
          <Flex alignItems="center" justifyContent="space-between">
            {!hideLabel && (
              <FieldLabel htmlFor={id}>
                {label}
                {required && <FieldRequiredIndicator />}
              </FieldLabel>
            )}
            {max !== undefined && <CharCount max={max} value={field.value} />}
          </Flex>
          <Input
            {...field}
            width={width}
            disabled={disabled}
            id={id}
            inputMode={inputMode}
            value={field.value}
          />
          {hint !== undefined && <FieldHelperText>{hint}</FieldHelperText>}
          {errors[name] !== undefined && (
            <FieldErrorText>{errors[name].message}</FieldErrorText>
          )}
        </FieldRoot>
      )}
    />
  )
}

const SelectField = ({
  label,
  hint,
  hover,
  required = false,
  error,
  htmlFor,
  children,
}: {
  label: string
  hint?: string
  /** 項目に hover した時に出す補足 (title)。 */
  hover?: string
  required?: boolean
  error?: string
  htmlFor: string
  children: ReactNode
}): React.ReactElement => (
  <FieldRoot
    alignItems="stretch"
    invalid={error !== undefined}
    required={required}
    title={hover}
  >
    <FieldLabel htmlFor={htmlFor}>
      {label}
      {required && <FieldRequiredIndicator />}
    </FieldLabel>
    {children}
    {hint !== undefined && <FieldHelperText>{hint}</FieldHelperText>}
    {error !== undefined && <FieldErrorText>{error}</FieldErrorText>}
  </FieldRoot>
)

/**
 * 保存で止まった時の focus 先。日付欄は DS の入力ではなく visually-hidden の native input と
 * 自前の popover なので RHF が ref を持てない。ラベルを見える位置へ寄せてから input に focus する
 * (RHF の shouldFocusError / setError の shouldFocus が呼ぶ focus() に載せる)。
 */
function focusDateField(id: string): void {
  document
    .querySelector(`label[for="${id}"]`)
    ?.scrollIntoView?.({ block: 'center' })
  document.getElementById(id)?.focus({ preventScroll: true })
}

export const DateField = ({
  control,
  errors,
  name,
  id,
  label,
  hint,
  required = false,
  disabled = false,
  hideLabel = false,
}: {
  control: Control<PlantUnitFormInput>
  errors: FieldErrors<PlantUnitFormInput>
  name: 'operationStartDate' | 'operationEndDate' | 'validFrom' | 'validTo'
  id: string
  label: string
  hint?: string
  required?: boolean
  disabled?: boolean
  hideLabel?: boolean
}): React.ReactElement => {
  const { field } = useController({ control, name })
  const { ref: registerRef } = field
  useEffect(() => {
    registerRef({ focus: () => focusDateField(id) })
  }, [registerRef, id])
  return (
    <FieldRoot
      alignItems="stretch"
      invalid={errors[name] !== undefined}
      required={required}
    >
      {!hideLabel && (
        <FieldLabel htmlFor={id}>
          {label}
          {required && <FieldRequiredIndicator />}
        </FieldLabel>
      )}
      <CompactCivilDateField
        disabled={disabled}
        hasClear={!required}
        hasError={errors[name] !== undefined}
        id={id}
        onChange={(d) => field.onChange(d)}
        value={(field.value ?? undefined) as CivilDate | undefined}
      />
      {hint !== undefined && <FieldHelperText>{hint}</FieldHelperText>}
      {errors[name] !== undefined && (
        <FieldErrorText>{errors[name].message}</FieldErrorText>
      )}
    </FieldRoot>
  )
}

/**
 * 電源ユニットの登録ダイアログ・詳細ページの編集で共通の入力欄。
 *
 * - 発電所は自社の発電所 (サイト) からの選択 (登録ダイアログはその場で追加もできる)。
 *   系統コードは広域機関の発電所からの選択 (任意)。どちらの候補もエリア欄で絞る
 *   (エリアは保存しない。ユニットのエリアは系統コードから決まる)
 * - 発電形式 (エネルギー源) と電源種別は別の軸で、どちらも必須
 * - 識別番号 3 つは採番元が違う別物なので 1 欄にまとめない
 * - 文字数の上限は入力中に削らず、欄ごとに `N / 上限` を出して保存時の文言で止める
 */
export const PlantUnitFormFields = ({
  control,
  errors,
  disabled = false,
  sites,
  plants,
  allowAddSite = false,
  pendingSite,
  onSiteAdded,
  idPrefix,
  showAreaFilter = true,
  onAreaChanged,
  onSiteChanged,
  powerTypeNoteAs = 'hint',
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const areaCode = useWatch({ control, name: 'areaCode' })
  const siteId = useWatch({ control, name: 'siteId' })
  const plantMasterId = useWatch({ control, name: 'plantMasterId' })
  const f = (key: string): string =>
    t(`page:adminPlantUnitMaster.unit.field.${key}`)

  // 候補を絞るエリア: エリア欄があればその値、無ければ (詳細ページ) 系統コードは選んだ発電所のエリア
  const siteAreaCode = sites.find((site) => site.id === siteId)?.areaCode
  const plantAreaCode = showAreaFilter
    ? areaCode
    : (siteAreaCode ?? AreaCode.UNSPECIFIED)
  // エリアを選ぶまで、発電所と系統コードは選べない (候補を絞る基準が無いため)
  const needsArea = showAreaFilter && areaCode === AreaCode.UNSPECIFIED
  const plantOptions = [
    { value: '', label: f('plantCodeNone') },
    ...plantCandidates(plants, {
      areaCode: plantAreaCode,
      currentPlantMasterId: plantMasterId,
    }).map((plant) => ({
      value: plant.id,
      label: `${plant.plantCode} ${plant.plantName}`,
    })),
  ]

  return (
    <Stack gap={4}>
      {showAreaFilter && (
        <SelectField
          error={errors.areaCode?.message}
          hint={f('areaFilterHint')}
          htmlFor={`${idPrefix}-area`}
          label={f('areaCode')}
        >
          {/* 保存しない絞り込み用の欄 */}
          <FormSelect
            aria-label={f('areaCode')}
            control={control}
            inputId={`${idPrefix}-area`}
            isDisabled={disabled}
            name="areaCode"
            numeric
            onValueChange={(raw) =>
              onAreaChanged?.(
                raw === undefined ? AreaCode.UNSPECIFIED : Number(raw),
              )
            }
            options={AREA_CODE_OPTIONS.map((code) => ({
              value: String(code),
              label: convertAreaLabel(code),
            }))}
          />
        </SelectField>
      )}

      <SelectField
        error={errors.siteId?.message}
        hint={f('siteHint')}
        htmlFor={`${idPrefix}-site`}
        label={f('site')}
        required
      >
        {/* 押せない理由は補足文でなく、押せない欄の tooltip で出す */}
        <ButtonTooltip content={needsArea ? f('areaFirstHint') : undefined}>
          <SiteSelectField
            allowAdd={allowAddSite}
            areaCode={showAreaFilter ? areaCode : AreaCode.UNSPECIFIED}
            control={control}
            currentSiteId={siteId}
            inputId={`${idPrefix}-site`}
            isDisabled={disabled || needsArea}
            onSiteAdded={onSiteAdded}
            onSiteChanged={onSiteChanged}
            pendingSite={pendingSite}
            sites={sites}
          />
        </ButtonTooltip>
      </SelectField>

      <TextField
        control={control}
        disabled={disabled}
        errors={errors}
        id={`${idPrefix}-unit-name`}
        label={f('unitName')}
        max={UNIT_NAME_MAX_LENGTH}
        name="unitName"
        required
      />
      <TextField
        control={control}
        disabled={disabled}
        errors={errors}
        hint={f('unitNameAbbrHint')}
        id={`${idPrefix}-unit-name-abbr`}
        label={f('unitNameAbbr')}
        max={UNIT_NAME_ABBR_MAX_LENGTH}
        name="unitNameAbbr"
        required
      />

      <SelectField
        error={errors.plantMasterId?.message}
        hint={f('plantCodeHint')}
        htmlFor={`${idPrefix}-plant`}
        label={f('plantCode')}
      >
        <ButtonTooltip content={needsArea ? f('areaFirstHint') : undefined}>
          <FormSelect
            aria-label={f('plantCode')}
            control={control}
            inputId={`${idPrefix}-plant`}
            isDisabled={disabled || needsArea}
            name="plantMasterId"
            options={plantOptions}
          />
        </ButtonTooltip>
      </SelectField>

      <SimpleGrid gap={4} columns={2}>
        <SelectField
          error={errors.generatorCategory?.message}
          hint={f('generatorCategoryHint')}
          htmlFor={`${idPrefix}-category`}
          label={f('generatorCategory')}
          required
        >
          <FormSelect
            aria-label={f('generatorCategory')}
            control={control}
            inputId={`${idPrefix}-category`}
            isDisabled={disabled}
            name="generatorCategory"
            numeric
            options={GENERATOR_CATEGORY_OPTIONS.map((category) => ({
              value: String(category),
              label: generatorCategoryLabel(t, category),
            }))}
          />
        </SelectField>
        <SelectField
          error={errors.powerType?.message}
          hint={powerTypeNoteAs === 'hover' ? undefined : f('powerTypeHint')}
          hover={powerTypeNoteAs === 'hover' ? f('powerTypeHint') : undefined}
          htmlFor={`${idPrefix}-power-type`}
          label={f('powerType')}
          required
        >
          <FormSelect
            aria-label={f('powerType')}
            control={control}
            inputId={`${idPrefix}-power-type`}
            isDisabled={disabled}
            name="powerType"
            numeric
            options={UNIT_POWER_TYPE_OPTIONS.map((powerType) => ({
              value: String(powerType),
              label: plantPowerTypeLabel(powerType),
            }))}
          />
        </SelectField>
      </SimpleGrid>

      <TextField
        width="200px"
        control={control}
        disabled={disabled}
        errors={errors}
        hint={f('authorizedOutputHint')}
        id={`${idPrefix}-output`}
        inputMode="numeric"
        label={f('authorizedOutputKw')}
        name="authorizedOutputKw"
        required
      />

      {/* 識別番号は 3 本とも採番元が違う別物: 受電地点特定番号 = TSO 採番 22 桁 /
          電源等識別番号 = 容量市場システム採番 / 発電者識別番号 = 当事者間の取り決め */}
      <TextField
        width="280px"
        control={control}
        disabled={disabled}
        errors={errors}
        hint={f('receivingPointIdHint')}
        id={`${idPrefix}-receiving-point-id`}
        inputMode="numeric"
        label={f('receivingPointId')}
        name="receivingPointId"
      />
      <TextField
        width="280px"
        control={control}
        disabled={disabled}
        errors={errors}
        hint={f('powerSourceIdHint')}
        id={`${idPrefix}-power-source-id`}
        label={f('powerSourceId')}
        max={POWER_SOURCE_ID_MAX_LENGTH}
        name="powerSourceId"
      />
      <TextField
        width="280px"
        control={control}
        disabled={disabled}
        errors={errors}
        hint={f('generatorIdentifierHint')}
        id={`${idPrefix}-generator-identifier`}
        label={f('generatorIdentifier')}
        max={GENERATOR_IDENTIFIER_MAX_LENGTH}
        name="generatorIdentifier"
      />

      <SimpleGrid gap={4} columns={2}>
        <DateField
          control={control}
          disabled={disabled}
          errors={errors}
          id={`${idPrefix}-operation-start`}
          label={f('operationStartDate')}
          name="operationStartDate"
          required
        />
        <DateField
          control={control}
          disabled={disabled}
          errors={errors}
          hint={f('operationEndDateHint')}
          id={`${idPrefix}-operation-end`}
          label={f('operationEndDate')}
          name="operationEndDate"
        />
        <DateField
          control={control}
          disabled={disabled}
          errors={errors}
          id={`${idPrefix}-valid-from`}
          label={f('validFrom')}
          name="validFrom"
          required
        />
        <DateField
          control={control}
          disabled={disabled}
          errors={errors}
          hint={f('validToHint')}
          id={`${idPrefix}-valid-to`}
          label={f('validTo')}
          name="validTo"
        />
      </SimpleGrid>

      <NoteField
        control={control}
        disabled={disabled}
        errors={errors}
        idPrefix={idPrefix}
      />
    </Stack>
  )
}

export const NoteField = ({
  control,
  disabled,
  errors,
  idPrefix,
  hideLabel = false,
}: {
  control: Control<PlantUnitFormInput>
  disabled?: boolean
  errors: FieldErrors<PlantUnitFormInput>
  idPrefix: string
  hideLabel?: boolean
}): React.ReactElement => {
  const { t } = useTranslation()
  const f = (key: string): string =>
    t(`page:adminPlantUnitMaster.unit.field.${key}`)
  return (
    <Controller
      control={control}
      name="note"
      render={({ field }) => (
        <FieldRoot alignItems="stretch" invalid={errors.note !== undefined}>
          <Flex alignItems="center" justifyContent="space-between">
            {!hideLabel && (
              <FieldLabel htmlFor={`${idPrefix}-note`}>{f('note')}</FieldLabel>
            )}
            <CharCount max={UNIT_NOTE_MAX_LENGTH} value={field.value} />
          </Flex>
          <Textarea
            {...field}
            disabled={disabled}
            id={`${idPrefix}-note`}
            rows={2}
          />
          {errors.note !== undefined && (
            <FieldErrorText>{errors.note.message}</FieldErrorText>
          )}
        </FieldRoot>
      )}
    />
  )
}
